import { useEffect, useRef, useState, type DragEvent } from 'react';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  DragOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Button,
  Card,
  Input,
  InputNumber,
  Modal,
  Select,
  Slider,
  Space,
  Switch,
  Tag,
  Typography,
  Upload,
} from 'antd';
import type { UploadProps } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import { api } from './api';
import { TeacherAvatarEditor } from './teacher-avatar-editor';
import {
  insertAtSelection,
  moveSelectionAfterEdit,
  type TextSelection,
} from './markdown-insertion';
import {
  getCropGeometry,
  getCropPreviewStyle,
  getImageCropProfile,
  moveCropPosition,
  type ImageCropProfile,
} from './image-crop';

export { getImageCropProfile } from './image-crop';

const { Text } = Typography;

export type EditableContentType =
  | 'course'
  | 'case'
  | 'teacher'
  | 'qr'
  | 'material'
  | 'home'
  | 'about'
  | 'navigation'
  | 'footer'
  | 'brand'
  | 'courseSettings'
  | 'materialSettings'
  | 'caseSettings'
  | 'teacherSettings';

type JsonObject = Record<string, unknown>;

type Section = { title: string; description?: string; keys: string[] };

const labels: Record<string, string> = {
  id: '系统内部标识',
  code: '页面网址（英文）',
  slug: '页面网址（英文）',
  slot: '使用位置标识',
  name: '名称',
  title: '标题',
  description: '说明',
  summary: '摘要',
  eyebrow: '栏目提示',
  badge: '角标',
  positioning: '课程定位',
  tags: '标签',
  audience: '适合人群',
  highlights: '课程亮点',
  price: '现价（分）',
  originalPrice: '原价（分，可留空）',
  cover: '封面图片',
  sort: '排序',
  sidebarNote: '封面补充说明',
  blocks: '课程内容区块',
  seo: '搜索引擎信息',
  ogImage: '分享图片',
  format: '资料格式',
  updated: '更新时间文案',
  claimed: '领取人数文案',
  body: '资料正文',
  includedTitle: '“包含内容”模块标题',
  included: '资料包含内容',
  suitableForTitle: '“适合谁”模块标题',
  suitableFor: '适合人群',
  howToClaimTitle: '“如何领取”模块标题',
  howToClaim: '领取步骤',
  qrCopy: '领取口令',
  qrCodes: '首页二维码配置',
  config: '二维码内容',
  consult: '咨询课程方案二维码',
  bottom: '底部添加老师二维码',
  meta: '学员背景',
  weeks: '备考周期',
  className: '班型',
  from: '起始分数',
  to: '最终分数',
  focus: '主攻科目',
  teacher: '负责老师',
  tag: '标签',
  after: '提分后成绩',
  before: '提分前成绩',
  l: '听力',
  r: '阅读',
  w: '写作',
  s: '口语',
  o: '总分',
  date: '出分日期',
  enroll: '报名时间',
  quote: '学员评价',
  background: '备考背景',
  backgroundTitle: '学员情况模块标题',
  path: '学习路径',
  pathTitle: '学习过程模块标题',
  review: '老师复盘',
  reviewTitle: '老师点评模块标题',
  galleryTitle: '学习群实录模块标题',
  galleryDescription: '学习群实录模块说明',
  shots: '学习群截图',
  category: '截图分类',
  caption: '截图说明',
  reportImage: '成绩单图片',
  reportTitle: '成绩单模块标题',
  score: '成绩/资历标签',
  subject: '授课科目',
  education: '教育背景',
  role: '职务/履历',
  painPoints: '擅长解决的问题',
  image: '图片',
  avatarCrop: '小头像取景',
  images: '图片',
  type: '类型',
  channelState: '渠道状态',
  leadFallback: '允许表单留资',
  lead: '设为领衔老师（全站仅一个）',
  leadProfile: '领衔老师专属内容',
  headline: '领衔老师标题',
  teachingTag: '授课标签',
  serviceValue: '服务数据',
  serviceLabel: '服务数据说明',
  credentials: '核心资历',
  experience: '执教经历',
  qrOverride: '独立二维码覆盖',
  hero: '首屏',
  highlightText: '标题橙色高亮文字',
  primaryCta: '主按钮文案',
  secondaryCta: '次按钮文案',
  primaryTarget: '主按钮点击后动作',
  secondaryTarget: '次按钮点击后动作',
  trust: '信任提示',
  stats: '数据指标',
  value: '数值',
  suffix: '数值后缀',
  aiTitle: 'AI 产品区标题',
  aiDescription: 'AI 产品区说明',
  aiProducts: 'AI 产品',
  key: '卡片展示类型（固定）',
  subtitle: '副标题',
  cta: '按钮文案',
  target: '点击后动作',
  kind: '动作类型',
  url: '外部链接',
  productDetail: '产品详解标题区',
  writingFeatures: 'AI 写作卖点',
  speakingFeatures: 'AI 口语卖点',
  chatWall: '学员群聊墙',
  rating: '好评数据',
  brandTrust: '品牌信任区',
  intro: '介绍',
  milestones: '发展历程',
  year: '年份',
  awards: '媒体奖项',
  organization: '颁发机构',
  certificates: '证书',
  thumbnail: '缩略图',
  process: '服务流程',
  items: '内容项',
  faqs: '常见问题',
  faqIntro: '常见问题介绍',
  bullets: '提示要点',
  activity: '底部活动条',
  enabled: '启用',
  text: '内容',
  button: '按钮文案',
  identity: '我们是谁',
  partnership: '官方合作',
  note: '补充说明',
  disclaimer: '声明',
  principles: '教育理念',
  methods: '教学方法',
  chat: '学习群截图',
  footnote: '图片说明',
  support: '学员支持',
  office: '办公环境',
  address: '地址',
  logo: '品牌 Logo',
  aiPlatformUrl: 'AI 学习平台地址',
  contact: '联系方式',
  leadSuccessMessage: '留资成功提示',
  footerDescription: '页脚品牌介绍',
  companyName: '公司名称',
  filingNumber: '备案号',
  userAgreementUrl: '用户协议地址',
  privacyPolicyUrl: '隐私政策地址',
  defaultQr: '默认二维码配置',
  href: '链接地址',
  label: '显示文字',
  hint: '补充提示',
  disabled: '禁用',
  links: '链接',
  anchor: '区块锚点',
  data: '区块内容',
  columns: '列数/表头',
  cards: '卡片',
  dashedLast: '最后一项使用虚线',
  emphasisCards: '重点卡片',
  steps: '步骤',
  rows: '表格行',
  fixedLeft: '固定首列',
  highlightColumn: '高亮列序号',
  limit: '引用数量',
};

export const contentEditorSections: Partial<Record<EditableContentType, Section[]>> = {
  course: [
    {
      title: '详情页首屏',
      description: '按照课程详情页顶部从上到下配置：页面地址、角标、课程名、定位文案和标签列表。',
      keys: ['code', 'badge', 'name', 'positioning', 'tags'],
    },
    {
      title: '价格与咨询信息',
      description: '现价和原价会并排显示；补充说明用于课程卡片和详情页右侧咨询栏。',
      keys: ['price', 'originalPrice', 'sidebarNote'],
    },
    {
      title: '课程列表与首页卡片',
      description: '卡片的适合人群读取下方“适合人群”区块，卖点读取首屏标签，不需要重复填写。',
      keys: ['cover'],
    },
    {
      title: '课程独立二维码',
      description:
        '开启后优先使用本课程二维码；未填写的内容依次使用课程兜底二维码和全站兜底二维码。',
      keys: [
        'qrOverride.enabled',
        'qrOverride.config.image',
        'qrOverride.config.title',
        'qrOverride.config.description',
      ],
    },
    {
      title: '详情页正文区块',
      description:
        '对应课程详情页首屏下方的正文，可拖拽或上下移动排序。无需再重复填写上面的列表卡片简介。',
      keys: ['blocks'],
    },
    {
      title: '搜索与分享',
      description:
        '用于搜索结果标题、摘要和微信等平台抓取链接时的分享封面；分享图不上传时自动使用课程封面或品牌 Logo。',
      keys: ['seo'],
    },
  ],
  material: [
    {
      title: '基础信息',
      keys: ['id', 'slug', 'title', 'summary', 'updated', 'claimed', 'sort'],
    },
    {
      title: '封面图片',
      description: '上传完整的图片封面，按 16:9 裁剪；封面不会自动叠加文字。',
      keys: ['cover'],
    },
    {
      title: '资料正文',
      description: '使用工具栏添加标题、列表、表格或正文图片。',
      keys: ['body'],
    },
    {
      title: '资料与领取说明',
      description: '三个模块的标题和内容均可独立配置，并按下方顺序展示。',
      keys: [
        'includedTitle',
        'included',
        'suitableForTitle',
        'suitableFor',
        'howToClaimTitle',
        'howToClaim',
        'qrCopy',
      ],
    },
    {
      title: '资料独立二维码',
      description:
        '开启后优先使用本资料二维码；未填写的内容依次使用资料兜底二维码和全站兜底二维码。',
      keys: [
        'qrOverride.enabled',
        'qrOverride.config.image',
        'qrOverride.config.title',
        'qrOverride.config.description',
      ],
    },
    {
      title: '搜索与分享',
      description: '用于搜索结果和链接分享；分享图不上传时自动使用资料封面或品牌 Logo。',
      keys: ['seo'],
    },
  ],
  case: [
    {
      title: '学员与课程信息',
      keys: [
        'id',
        'name',
        'meta',
        'weeks',
        'className',
        'focus',
        'teacher',
        'tag',
        'enroll',
        'date',
      ],
    },
    { title: '提分成绩', keys: ['from', 'to', 'before', 'after'] },
    {
      title: '学员情况',
      keys: ['backgroundTitle', 'background'],
    },
    {
      title: '学习过程',
      description: '标题、步骤标题和步骤内容都会直接对应前端，并可调整步骤顺序。',
      keys: ['pathTitle', 'path'],
    },
    {
      title: '老师点评与学员评价',
      keys: ['reviewTitle', 'review', 'quote'],
    },
    {
      title: '学习群实录',
      description: '可配置模块标题、说明和截图；截图支持排序，最多 20 张。',
      keys: ['galleryTitle', 'galleryDescription', 'shots'],
    },
    { title: '成绩单', keys: ['reportTitle', 'reportImage'] },
    {
      title: '出分案例独立二维码',
      description:
        '开启后优先使用本案例二维码；未填写的内容依次使用出分案例兜底二维码和全站兜底二维码。',
      keys: [
        'qrOverride.enabled',
        'qrOverride.config.image',
        'qrOverride.config.title',
        'qrOverride.config.description',
      ],
    },
    {
      title: '搜索与分享',
      description:
        '用于搜索结果和分享链接的信息，不是弹窗内容。分享图不上传时自动使用成绩单图片或品牌 Logo。',
      keys: ['seo'],
    },
  ],
  teacher: [
    {
      title: '老师类型',
      description: '请先选择老师类型。领衔老师会显示横向大卡片和鼠标悬浮内容，全站只能设置一位。',
      keys: ['lead'],
    },
    { title: '教师信息', keys: ['name', 'score', 'subject', 'education', 'role'] },
    { title: '老师照片', description: '上传后可按官网展示比例裁剪。', keys: ['image'] },
    {
      title: '团队成员小头像',
      description: '使用老师照片单独调整鼠标悬浮时的小头像取景。',
      keys: ['avatarCrop'],
    },
    {
      title: '领衔老师卡片内容',
      description: '仅领衔老师使用；配置卡片正面显示的标题、标签和服务数据。',
      keys: [
        'leadProfile.headline',
        'leadProfile.teachingTag',
        'leadProfile.serviceValue',
        'leadProfile.serviceLabel',
      ],
    },
    {
      title: '领衔老师鼠标悬浮内容',
      description:
        '核心资历和执教经历会显示在鼠标悬浮层；下方“教学介绍”会同时作为悬浮层的教学特点。',
      keys: ['leadProfile.credentials', 'leadProfile.experience'],
    },
    { title: '教学介绍', keys: ['painPoints', 'description'] },
    {
      title: '老师独立二维码',
      description:
        '开启后优先使用本老师二维码；未填写的内容依次使用师资兜底二维码和全站兜底二维码。',
      keys: [
        'qrOverride.enabled',
        'qrOverride.config.image',
        'qrOverride.config.title',
        'qrOverride.config.description',
      ],
    },
  ],
  qr: [{ title: '二维码内容', keys: ['image', 'title', 'description'] }],
  courseSettings: [
    {
      title: '课程兜底咨询二维码',
      description: '所有未启用独立二维码的课程使用这里；空字段继续使用全站兜底。',
      keys: ['defaultQr.image', 'defaultQr.title', 'defaultQr.description'],
    },
  ],
  materialSettings: [
    {
      title: '资料兜底领取二维码',
      description: '所有未启用独立二维码的资料使用这里；空字段继续使用全站兜底。',
      keys: ['defaultQr.image', 'defaultQr.title', 'defaultQr.description'],
    },
  ],
  caseSettings: [
    {
      title: '出分案例兜底咨询二维码',
      description: '所有未启用独立二维码的出分案例使用这里；空字段继续使用全站兜底。',
      keys: ['defaultQr.image', 'defaultQr.title', 'defaultQr.description'],
    },
  ],
  teacherSettings: [
    {
      title: '师资兜底咨询二维码',
      description: '所有未启用独立二维码的老师使用这里；空字段继续使用全站兜底。',
      keys: ['defaultQr.image', 'defaultQr.title', 'defaultQr.description'],
    },
  ],
  home: [
    {
      title: '首屏',
      description: '合作伙伴资质和右侧测评演示为固定样式，无需上传首屏图片。',
      keys: ['hero'],
    },
    { title: '核心数据', keys: ['stats'] },
    {
      title: 'AI 产品',
      keys: [
        'aiTitle',
        'aiDescription',
        'aiProducts',
        'productDetail',
        'writingFeatures',
        'speakingFeatures',
      ],
    },
    { title: '学员群聊墙', keys: ['chatWall'] },
    { title: '品牌信任', keys: ['brandTrust'] },
    { title: '服务流程', keys: ['process'] },
    { title: '常见问题', keys: ['faqIntro', 'faqs'] },
    { title: '底部留资', keys: ['lead', 'qrCodes.bottom'] },
    { title: '活动条', keys: ['activity'] },
  ],
  about: [
    { title: '页面介绍', keys: ['eyebrow', 'title', 'intro', 'stats', 'identity', 'milestones'] },
    { title: '官方合作', keys: ['partnership'] },
    { title: '教育理念与方法', keys: ['principles', 'methods'] },
    { title: '学员支持与群聊', keys: ['chat', 'support'] },
    { title: '荣誉资质', keys: ['certificates', 'awards'] },
    { title: '办公环境', keys: ['office'] },
  ],
  brand: [
    { title: '品牌基础信息', keys: ['name', 'logo', 'aiPlatformUrl'] },
    {
      title: '提交反馈',
      keys: ['leadSuccessMessage'],
    },
    {
      title: '公司与备案信息',
      description: '这些信息显示在全站页脚底部，但属于公司统一资料，不需要在页脚栏目中重复配置。',
      keys: ['footerDescription', 'companyName', 'filingNumber'],
    },
    {
      title: '法律信息',
      description: '用户协议和隐私政策链接显示在全站页脚底部。',
      keys: ['userAgreementUrl', 'privacyPolicyUrl'],
    },
    {
      title: '全站弹窗文案与最终兜底二维码',
      description:
        '所有二维码弹窗统一使用这里的标题和说明；对应模块未上传二维码图片时，也会使用这里的图片。',
      keys: ['defaultQr.image', 'defaultQr.title', 'defaultQr.description'],
    },
    { title: '统计脚本（如已配置）', keys: ['analytics'] },
  ],
};

const longTextKeys = new Set([
  'description',
  'summary',
  'audience',
  'sidebarNote',
  'quote',
  'background',
  'review',
  'galleryDescription',
  'intro',
  'identity',
  'support',
  'disclaimer',
  'footerDescription',
  'leadSuccessMessage',
]);

const imageKeys = new Set(['cover', 'image', 'logo', 'ogImage', 'thumbnail', 'reportImage']);

const enumOptions: Record<string, Array<{ label: string; value: string }>> = {
  type: [
    { label: '企业微信活码', value: 'wecom_live' },
    { label: '个人微信', value: 'personal_wechat' },
    { label: '小程序码', value: 'mini_program' },
    { label: '公众号', value: 'official_account' },
  ],
};

function isObject(value: unknown): value is JsonObject {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function clone<T>(value: T): T {
  return structuredClone(value);
}

function nestedValue(value: JsonObject, key: string) {
  return key.split('.').reduce<unknown>((current, part) => {
    return isObject(current) ? current[part] : undefined;
  }, value);
}

function setNestedValue(value: JsonObject, key: string, next: unknown): JsonObject {
  const [head, ...rest] = key.split('.');
  if (!head) return value;
  if (!rest.length) return { ...value, [head]: next };
  const child = isObject(value[head]) ? value[head] : {};
  return { ...value, [head]: setNestedValue(child, rest.join('.'), next) };
}

function orderedObjectKeys(value: JsonObject, path: string[], keys?: string[]) {
  if (keys)
    return keys.filter(
      (key) =>
        (path.length === 1 && path[0] === 'teacher' && key === 'avatarCrop') ||
        nestedValue(value, key) !== undefined,
    );
  const joined = path.join('.').replace(/\.\d+/g, '.*');
  const preferred: Record<string, string[]> = {
    'home.hero': ['title', 'highlightText', 'description', 'primaryCta', 'secondaryCta', 'trust'],
    'home.aiProducts.*': [
      'name',
      'badge',
      'subtitle',
      'description',
      'tags',
      'cta',
      'target',
      'key',
    ],
    'home.writingFeatures.*': ['title', 'description'],
    'home.speakingFeatures.*': ['title', 'description'],
    'home.process.*': ['title', 'items'],
    'home.faqIntro': [
      'description',
      'bullets',
      'primaryCta',
      'primaryTarget',
      'secondaryCta',
      'secondaryTarget',
    ],
    'home.lead': ['title', 'description', 'button', 'hint'],
  };
  const order = preferred[joined];
  const hidden = new Set(
    joined === 'home.hero'
      ? ['eyebrow']
      : joined === 'home.writingFeatures.*' || joined === 'home.speakingFeatures.*'
        ? ['panel']
        : [],
  );
  const available = Object.keys(value).filter((key) => !hidden.has(key));
  if (joined === 'brand.defaultQr')
    return ['title', 'description', 'image'].filter((key) => key in value);
  if ('image' in value && 'title' in value && 'description' in value) {
    const qrOrder = ['title', 'description', 'image'];
    return [
      ...qrOrder.filter((key) => key in value),
      ...available.filter((key) => !qrOrder.includes(key)),
    ];
  }
  if (!order) return available;
  return [
    ...order.filter((key) => key in value),
    ...available.filter((key) => !order.includes(key)),
  ];
}

function createQrConfig() {
  return {
    image: '',
    title: '微信扫码，添加规划老师',
    description: '课程咨询 · 内测名额 · 测评入口，一对一回复',
  };
}

const aiBadgeDefaults: Record<string, string> = {
  plan: '免费预约',
  writing: '已上线',
  speaking: '已上线',
  listening: '内测中',
};

export function createContentDefault(type: EditableContentType): unknown {
  const seo = { title: '请填写页面标题', description: '请填写页面摘要' };
  const score = { l: '0.0', r: '0.0', w: '0.0', s: '0.0', o: '0.0' };
  const defaults: Partial<Record<EditableContentType, unknown>> = {
    course: {
      id: `course-${crypto.randomUUID().slice(0, 8)}`,
      code: 'new-course',
      name: '新课程',
      badge: '新课程',
      positioning: '',
      tags: [],
      audience: '',
      highlights: [],
      price: 0,
      originalPrice: null,
      cover: '',
      sort: 0,
      sidebarNote: '',
      qrOverride: { enabled: false, config: createQrConfig() },
      blocks: [],
      seo,
    },
    material: {
      id: `material-${crypto.randomUUID().slice(0, 8)}`,
      slug: 'new-material',
      title: '新资料',
      summary: '',
      updated: '',
      claimed: '',
      cover: '',
      body: '',
      includedTitle: '包含内容',
      included: [],
      suitableForTitle: '适合谁',
      suitableFor: [],
      howToClaimTitle: '如何领取',
      howToClaim: [],
      qrCopy: '',
      qrOverride: { enabled: false, config: createQrConfig() },
      sort: 0,
      seo,
    },
    case: {
      id: 'new-case',
      name: '新案例',
      meta: '',
      weeks: '',
      className: '',
      from: '0.0',
      to: '0.0',
      focus: '',
      teacher: '',
      tag: '',
      after: score,
      before: clone(score),
      date: '',
      enroll: '',
      quote: '',
      backgroundTitle: '学员情况介绍',
      background: '',
      pathTitle: '学习过程',
      path: [],
      reviewTitle: '老师点评',
      review: '',
      galleryTitle: '学习群实录',
      galleryDescription:
        '学员基础、学习过程、老师指导、班主任督学到提分报喜，均来自学员学习群真实记录 · 已获学员授权',
      shots: [],
      reportTitle: '雅思成绩单',
      reportImage: '',
      qrOverride: { enabled: false, config: createQrConfig() },
      sort: 0,
      seo,
    },
    teacher: {
      id: `teacher-${crypto.randomUUID().slice(0, 8)}`,
      name: '',
      score: '',
      subject: '',
      education: '',
      role: '',
      painPoints: ['', ''],
      description: '',
      image: '',
      lead: false,
      leadProfile: {
        headline: '',
        teachingTag: '',
        serviceValue: '',
        serviceLabel: '',
        credentials: [],
        experience: [],
      },
      qrOverride: { enabled: false, config: createQrConfig() },
      sort: 0,
    },
    qr: {
      slot: 'new-qr-slot',
      image: '',
      title: '新二维码',
      description: '',
      type: 'wecom_live',
      channelState: '',
      leadFallback: true,
    },
    courseSettings: { defaultQr: createQrConfig() },
    materialSettings: { defaultQr: createQrConfig() },
    caseSettings: { defaultQr: createQrConfig() },
    teacherSettings: { defaultQr: createQrConfig() },
    navigation: [],
    footer: [],
  };
  return clone(defaults[type] ?? {});
}

export function prepareContentForEditor(type: EditableContentType, data: unknown): unknown {
  const prepared = clone(data);
  const simplifiedQrTypes: EditableContentType[] = [
    'course',
    'material',
    'case',
    'teacher',
    'home',
    'courseSettings',
    'materialSettings',
    'caseSettings',
    'teacherSettings',
  ];
  const visit = (value: unknown) => {
    if (Array.isArray(value)) {
      value.forEach(visit);
      return;
    }
    if (!isObject(value)) return;
    if ('key' in value && typeof value.key === 'string' && !('badge' in value))
      value.badge = aiBadgeDefaults[value.key] ?? '';
    if ('key' in value && typeof value.key === 'string' && 'status' in value) delete value.status;
    if ('seo' in value && isObject(value.seo) && !('ogImage' in value.seo)) value.seo.ogImage = '';
    if (
      simplifiedQrTypes.includes(type) &&
      'image' in value &&
      'title' in value &&
      'description' in value
    ) {
      delete value.type;
      delete value.channelState;
      delete value.leadFallback;
      delete value.slot;
    }
    Object.values(value).forEach(visit);
  };
  visit(prepared);
  if (
    isObject(prepared) &&
    ['course', 'material', 'case', 'teacher'].includes(type) &&
    !('qrOverride' in prepared)
  )
    prepared.qrOverride = { enabled: false, config: createQrConfig() };
  if (type === 'teacher' && isObject(prepared) && !('leadProfile' in prepared))
    prepared.leadProfile = {
      headline: '',
      teachingTag: '',
      serviceValue: '',
      serviceLabel: '',
      credentials: [],
      experience: [],
    };
  if (type === 'case' && isObject(prepared)) {
    prepared.backgroundTitle ??= '学员情况介绍';
    prepared.pathTitle ??= '学习过程';
    prepared.reviewTitle ??= '老师点评';
    prepared.reportTitle ??= '雅思成绩单';
  }
  if (type === 'home' && isObject(prepared) && !('qrCodes' in prepared))
    prepared.qrCodes = {
      teacher: createQrConfig(),
      consult: createQrConfig(),
      bottom: createQrConfig(),
    };
  if (type === 'home' && isObject(prepared) && isObject(prepared.hero)) {
    prepared.hero.highlightText ??= 'AI';
    prepared.hero.primaryTarget ??= { kind: 'qr', slot: 'home-consult' };
    prepared.hero.secondaryTarget ??= { kind: 'anchor', id: 'sec-course' };
  }
  if (type === 'home' && isObject(prepared) && isObject(prepared.faqIntro)) {
    prepared.faqIntro.primaryCta ??= '咨询课程顾问';
    prepared.faqIntro.primaryTarget ??= { kind: 'qr', slot: 'home-consult' };
    prepared.faqIntro.secondaryTarget ??= { kind: 'anchor', id: 'sec-lead' };
  }
  if (type === 'qr' && isObject(prepared) && !('channelState' in prepared))
    prepared.channelState = '';
  return prepared;
}

export function sanitizeContentForSave(data: unknown): unknown {
  if (Array.isArray(data)) return data.map(sanitizeContentForSave);
  if (!isObject(data)) return data;
  return Object.fromEntries(
    Object.entries(data)
      .filter(([key, value]) => !(key === 'ogImage' && value === ''))
      .map(([key, value]) => [key, sanitizeContentForSave(value)]),
  );
}

export function resourceKeyFor(type: EditableContentType, data: unknown) {
  if (!isObject(data)) return '';
  const keyField: Partial<Record<EditableContentType, string>> = {
    course: 'code',
    material: 'slug',
    case: 'id',
    teacher: 'id',
    qr: 'slot',
  };
  return String(data[keyField[type] ?? 'id'] ?? '');
}

export function contentDisplayName(data: unknown, fallback: string) {
  if (Array.isArray(data)) return `${data.length} 项配置`;
  if (!isObject(data)) return fallback;
  return String(data.name ?? data.title ?? fallback);
}

function fieldLabel(key: string, value: unknown, path: string[]) {
  const scope = path[0];
  if (key === 'defaultQr' && scope === 'brand') return '全站通用弹窗二维码';
  if (key === 'id' && scope === 'case') return '案例页面网址（英文，创建后锁定）';
  if (key === 'id' && (scope === 'course' || scope === 'material'))
    return '系统内部标识（自动生成）';
  if (key === 'code' && scope === 'course') return '课程页面网址（英文，创建后锁定）';
  if (key === 'slug' && scope === 'material') return '资料页面网址（英文，创建后锁定）';
  if (key === 'ogImage') return '分享封面（可选）';
  if (key === 'lead' && isObject(value)) return '底部留资配置';
  if (key === 'badge' && path.includes('aiProducts')) return '左上角标签';
  if (key === 'teacher' && path.includes('qrCodes')) return '预约老师二维码';
  if (key === 'consult' && path.includes('qrCodes')) return '通用咨询二维码';
  if (key === 'bottom' && path.includes('qrCodes')) return '底部留资二维码';
  if (key === 'enabled' && path.includes('qrOverride')) return '启用独立二维码';
  if (key === 'image' && path.includes('qrOverride')) return '独立二维码图片';
  if (key === 'title' && path.includes('qrOverride')) return '独立二维码弹窗标题';
  if (key === 'description' && path.includes('qrOverride')) return '独立二维码弹窗说明';
  if (key === 'image' && path.includes('defaultQr')) return '兜底二维码图片';
  if (key === 'title' && path.includes('defaultQr')) return '兜底二维码弹窗标题';
  if (key === 'description' && path.includes('defaultQr')) return '兜底二维码弹窗说明';
  if (key === 'image' && path.includes('qrCodes') && path.includes('teacher'))
    return '师资统一二维码图片';
  if (key === 'image' && path.includes('qrCodes') && path.includes('consult'))
    return '首页兜底二维码图片';
  if (key === 'title' && path.includes('path')) return '步骤标题';
  if (key === 'text' && path.includes('path')) return '步骤内容';
  return labels[key] ?? key;
}

function fieldHelp(fieldKey: string, path: string[]) {
  const scope = path[0];
  if (fieldKey === 'id' && (scope === 'course' || scope === 'material'))
    return '用于系统关联数据，自动生成，运营人员无需修改。';
  if (fieldKey === 'code' && scope === 'course')
    return '新建时可自定义，例如 qihang，对应 /courses/qihang/。创建后锁定，避免旧链接失效。';
  if (fieldKey === 'slug' && scope === 'material')
    return '新建时可自定义，例如 writing-samples，对应 /materials/writing-samples/。创建后锁定。';
  if (fieldKey === 'id' && scope === 'case')
    return '新建时可自定义，例如 student-7-band，对应 /cases/student-7-band/。创建后锁定。';
  if (fieldKey === 'ogImage')
    return '建议 1200×630。留空时会依次使用当前内容封面、成绩单图片或品牌 Logo。';
  if (fieldKey === 'highlightText' && scope === 'home')
    return '填写标题中需要显示为橙色的文字，例如 AI；留空则不高亮。';
  if (fieldKey === 'title' && path.join('.') === 'home.hero.title')
    return '建议填写两行：在需要换行的位置按回车，官网会按行展示。';
  return '';
}

function fieldValidation(fieldKey: string, value: unknown) {
  if (typeof value !== 'string') return '';
  if (fieldKey === 'code' && !/^[a-z][a-z0-9-]*$/.test(value))
    return '请以小写字母开头，只使用小写字母、数字和短横线。';
  if (fieldKey === 'slug' && !/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value))
    return '只使用小写字母、数字和短横线，且不能以短横线开头或结尾。';
  if (fieldKey === 'anchor' && !/^[a-z][a-z0-9-]*$/.test(value))
    return '请以小写字母开头，只使用小写字母、数字和短横线。';
  if (fieldKey === 'slot' && !value.trim()) return '请填写使用位置标识。';
  if ((fieldKey === 'name' || fieldKey === 'title') && !value.trim()) return '此项不能为空。';
  return '';
}

function fixedArrayLength(path: string[]) {
  const value = path.join('.');
  if (value.endsWith('aiProducts')) return 4;
  if (value.endsWith('office.images')) return 3;
  if (value.endsWith('painPoints')) return 2;
  return null;
}

const maxCaseScreenshots = 20;

function maxArrayLength(path: string[]) {
  const value = path.join('.').replace(/\.\d+\./g, '.');
  const limits: Record<string, number> = {
    'course.tags': 8,
    'course.highlights': 8,
    'case.path': 8,
    'case.shots': maxCaseScreenshots,
    'home.stats': 4,
    'home.writingFeatures': 6,
    'home.speakingFeatures': 6,
    'home.chatWall.images': 20,
    'home.brandTrust.milestones': 12,
    'home.brandTrust.awards': 12,
    'home.brandTrust.certificates': 12,
    'home.process': 5,
    'home.faqs': 12,
    'home.faqIntro.bullets': 5,
    'about.stats': 4,
    'about.milestones': 10,
    'about.partnership.items': 8,
    'about.principles': 4,
    'about.methods': 8,
    'about.chat.images': 10,
    'about.certificates': 12,
    'about.awards': 12,
    'blocks.data.emphasisCards': 3,
    'blocks.data.steps': 12,
  };
  return limits[value] ?? null;
}

function emptyItem(path: string[], sample?: unknown): unknown {
  const joined = path.join('.');
  if (joined === 'about.stats') return { value: '', label: '' };
  if (joined.endsWith('stats')) return { value: 0, suffix: '', title: '', description: '' };
  if (joined.endsWith('aiProducts'))
    return {
      key: '',
      badge: '免费预约',
      name: '',
      subtitle: '',
      description: '',
      tags: [],
      cta: '',
      target: { kind: 'qr', slot: 'global-default' },
    };
  if (joined.endsWith('writingFeatures') || joined.endsWith('speakingFeatures'))
    return { title: '', description: '' };
  if (joined.endsWith('milestones')) return { year: '', text: '' };
  if (joined.endsWith('awards')) return { organization: '', title: '', year: '' };
  if (joined.endsWith('certificates')) return { title: '', thumbnail: '', image: '' };
  if (joined.endsWith('process')) return { title: '', items: [] };
  if (joined.endsWith('faqs')) return ['', ''];
  if (joined.endsWith('principles') || joined.endsWith('methods') || joined.endsWith('path'))
    return { title: '', text: '' };
  if (joined.endsWith('shots')) return { image: '', category: '', caption: '' };
  if (joined.endsWith('navigation')) return { label: '', href: '/', disabled: false };
  if (joined.endsWith('footer')) return { title: '', links: [] };
  if (joined.endsWith('links')) return { label: '', href: '/' };
  if (joined.endsWith('cards') || joined.endsWith('emphasisCards') || joined.endsWith('steps'))
    return { title: '', text: '' };
  if (joined.endsWith('rows')) return [];
  if (isObject(sample))
    return Object.fromEntries(
      Object.entries(sample).map(([key, value]) => [
        key,
        typeof value === 'boolean'
          ? false
          : typeof value === 'number'
            ? 0
            : Array.isArray(value)
              ? []
              : '',
      ]),
    );
  if (Array.isArray(sample)) return sample.map(() => '');
  return '';
}

async function uploadImage(file: File, scope: EditableContentType, kind?: string) {
  const form = new FormData();
  form.append('file', file);
  const params = new URLSearchParams({ scope });
  if (kind) params.set('kind', kind);
  return api<{ public_url: string }>(`/assets/upload?${params}`, {
    method: 'POST',
    body: form,
  });
}

export async function uploadCaseScreenshots(
  files: readonly File[],
  getCurrent: () => unknown[],
  onChange: (screenshots: unknown[]) => void,
) {
  const remaining = Math.max(0, maxCaseScreenshots - getCurrent().length);
  if (files.length > remaining)
    return [`最多 ${maxCaseScreenshots} 张截图，当前还可上传 ${remaining} 张，请重新选择。`];

  const errors: string[] = [];
  for (const file of files) {
    if (getCurrent().length >= maxCaseScreenshots) {
      errors.push(`已达到 ${maxCaseScreenshots} 张截图上限，剩余截图未上传。`);
      break;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      errors.push(`${file.name}：仅支持 JPG、PNG 或 WebP。`);
      continue;
    }
    if (file.size > 15 * 1024 * 1024) {
      errors.push(`${file.name}：图片不能超过 15MB。`);
      continue;
    }
    try {
      const result = await uploadImage(file, 'case');
      const current = getCurrent();
      if (current.length >= maxCaseScreenshots) {
        errors.push(`已达到 ${maxCaseScreenshots} 张截图上限，${file.name} 未添加。`);
        break;
      }
      onChange([...current, { image: result.public_url, category: '', caption: '' }]);
    } catch (error) {
      errors.push(`${file.name}：${error instanceof Error ? error.message : '上传失败'}`);
    }
  }
  return errors;
}

async function cropImage(
  file: File,
  profile: ImageCropProfile,
  zoom: number,
  x: number,
  y: number,
) {
  const url = URL.createObjectURL(file);
  try {
    const image = new Image();
    image.src = url;
    await image.decode();
    const { cropWidth, cropHeight, maxX, maxY } = getCropGeometry(
      image.naturalWidth,
      image.naturalHeight,
      profile.width / profile.height,
      zoom,
    );
    const sourceX = ((x + 100) / 200) * maxX;
    const sourceY = ((y + 100) / 200) * maxY;
    const canvas = document.createElement('canvas');
    canvas.width = profile.width;
    canvas.height = profile.height;
    const context = canvas.getContext('2d');
    if (!context) throw new Error('当前浏览器不支持图片裁剪');
    context.drawImage(
      image,
      sourceX,
      sourceY,
      cropWidth,
      cropHeight,
      0,
      0,
      canvas.width,
      canvas.height,
    );
    const blob = await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (result) => (result ? resolve(result) : reject(new Error('图片裁剪失败'))),
        'image/webp',
        0.9,
      ),
    );
    return new File([blob], `${file.name.replace(/\.[^.]+$/, '')}-cropped.webp`, {
      type: 'image/webp',
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function ImageUploadField({
  value,
  onChange,
  scope,
  kind,
  teacherLead = false,
  imageOrigin,
}: {
  value: string;
  onChange: (value: string) => void;
  scope: EditableContentType;
  kind?: string;
  teacherLead?: boolean;
  imageOrigin?: string;
}) {
  const preserveTeacherPhoto = kind === 'teacher' && !teacherLead;
  const previewSrc =
    preserveTeacherPhoto && imageOrigin && value.startsWith('/')
      ? new URL(value, imageOrigin).href
      : value;
  const [error, setError] = useState('');
  const [cropRequest, setCropRequest] = useState<{
    file: File;
    onSuccess?: (body: unknown) => void;
    onError?: (error: Error) => void;
  } | null>(null);
  const [cropPreview, setCropPreview] = useState('');
  const [cropZoom, setCropZoom] = useState(1);
  const [cropX, setCropX] = useState(0);
  const [cropY, setCropY] = useState(0);
  const [cropSize, setCropSize] = useState<{ width: number; height: number } | null>(null);
  const [cropDragging, setCropDragging] = useState(false);
  const cropDrag = useRef<{
    pointerId: number;
    clientX: number;
    clientY: number;
    x: number;
    y: number;
    overflowX: number;
    overflowY: number;
  } | null>(null);
  const [uploading, setUploading] = useState(false);
  const cropProfile = getImageCropProfile(kind, {
    lead: teacherLead,
  });
  const cropPreviewScale = cropProfile?.previewScale ?? 1;
  const cropGeometry =
    cropSize && cropProfile
      ? getCropGeometry(
          cropSize.width,
          cropSize.height,
          cropProfile.width / cropProfile.height,
          cropZoom,
        )
      : null;
  useEffect(() => {
    cropDrag.current = null;
    setCropDragging(false);
    setCropSize(null);
    if (!cropRequest) {
      setCropPreview('');
      return;
    }
    const url = URL.createObjectURL(cropRequest.file);
    setCropPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [cropRequest]);
  const finishUpload = async (
    file: File,
    onSuccess?: (body: unknown) => void,
    onError?: (error: Error) => void,
  ) => {
    try {
      setUploading(true);
      setError('');
      const result = await uploadImage(file, scope, kind);
      onChange(result.public_url);
      onSuccess?.(result);
      setCropRequest(null);
    } catch (uploadError) {
      const message = uploadError instanceof Error ? uploadError.message : '上传失败';
      setError(message);
      onError?.(uploadError as Error);
    } finally {
      setUploading(false);
    }
  };
  const props: UploadProps = {
    accept: 'image/jpeg,image/png,image/webp',
    maxCount: 1,
    showUploadList: false,
    customRequest: async (options) => {
      const file = options.file as File;
      if (cropProfile && !preserveTeacherPhoto) {
        setCropZoom(1);
        setCropX(0);
        setCropY(0);
        setCropRequest({
          file,
          onSuccess: options.onSuccess,
          onError: options.onError,
        });
        return;
      }
      await finishUpload(file, options.onSuccess, options.onError);
    },
  };
  return (
    <div className={`image-field${preserveTeacherPhoto ? ' teacher-member-image-field' : ''}`}>
      {value ? (
        <img src={previewSrc} alt="当前图片预览" />
      ) : (
        <div className="image-empty">暂未上传</div>
      )}
      <Space wrap>
        <Upload {...props}>
          <Button icon={<UploadOutlined />}>{value ? '替换图片' : '上传图片'}</Button>
        </Upload>
        {preserveTeacherPhoto && (
          <Upload
            {...props}
            customRequest={(options) => {
              setCropZoom(1);
              setCropX(0);
              setCropY(0);
              setCropRequest({
                file: options.file as File,
                onSuccess: options.onSuccess,
                onError: options.onError,
              });
            }}
          >
            <Button icon={<UploadOutlined />}>裁剪后上传</Button>
          </Upload>
        )}
        {value && (
          <Button danger onClick={() => onChange('')}>
            移除
          </Button>
        )}
      </Space>
      <Text type="secondary">
        {kind === 'qr'
          ? '建议上传清晰的正方形二维码；系统会处理为 600×600 WebP，原图最大 5MB。'
          : kind === 'teacher'
            ? teacherLead
              ? '领衔老师使用圆形头像；上传前裁剪为 900×900，预览包含官网放大效果，原图最大 15MB。'
              : '团队成员照片默认完整保留，官网完整显示；也可选择裁剪后上传。鼠标悬浮时的小头像可在下方独立调整取景，原图最大 15MB。'
            : kind === 'share'
              ? '上传后先裁剪为 1200×630；原图最大 8MB。留空会自动使用页面封面或品牌 Logo。'
              : kind === 'course-cover'
                ? '课程封面统一按 16:9 裁剪为 1200×675；首页、课程列表、详情页及手机端比例一致，原图最大 8MB。'
                : kind === 'material-cover'
                  ? '资料封面按桌面端 16:9 裁剪为 1200×675；其他屏幕尺寸自动适配，原图最大 8MB。'
                  : 'JPG、PNG 或 WebP，最大 15MB。'}
      </Text>
      {error && <Alert type="error" showIcon title={error} />}
      <Modal
        title={cropProfile?.title ?? '裁剪图片'}
        open={!!cropRequest}
        okText="确认裁剪并上传"
        cancelText="取消"
        confirmLoading={uploading}
        onCancel={() => setCropRequest(null)}
        onOk={async () => {
          if (!cropRequest) return;
          if (!cropProfile) return;
          try {
            const cropped = await cropImage(cropRequest.file, cropProfile, cropZoom, cropX, cropY);
            await finishUpload(cropped, cropRequest.onSuccess, cropRequest.onError);
          } catch (cropError) {
            setError(cropError instanceof Error ? cropError.message : '图片裁剪失败');
          }
        }}
      >
        <div className="image-crop-editor">
          {cropProfile && <Text type="secondary">{cropProfile.description}</Text>}
          <div
            className={`image-crop-frame${cropProfile && cropProfile.width < cropProfile.height ? ' is-portrait' : ''}${cropDragging ? ' is-dragging' : ''}`}
            onPointerDown={(event) => {
              if (!cropGeometry || uploading || !event.isPrimary || event.button !== 0) return;
              const frame = event.currentTarget.getBoundingClientRect();
              event.currentTarget.setPointerCapture(event.pointerId);
              cropDrag.current = {
                pointerId: event.pointerId,
                clientX: event.clientX,
                clientY: event.clientY,
                x: cropX,
                y: cropY,
                overflowX: frame.width * (cropGeometry.widthPercent / 100 - 1) * cropPreviewScale,
                overflowY: frame.height * (cropGeometry.heightPercent / 100 - 1) * cropPreviewScale,
              };
              setCropDragging(true);
              event.preventDefault();
            }}
            onPointerMove={(event) => {
              const drag = cropDrag.current;
              if (!drag || drag.pointerId !== event.pointerId) return;
              setCropX(moveCropPosition(drag.x, event.clientX - drag.clientX, drag.overflowX));
              setCropY(moveCropPosition(drag.y, event.clientY - drag.clientY, drag.overflowY));
            }}
            onPointerUp={(event) => {
              if (event.currentTarget.hasPointerCapture(event.pointerId))
                event.currentTarget.releasePointerCapture(event.pointerId);
              cropDrag.current = null;
              setCropDragging(false);
            }}
            onPointerCancel={() => {
              cropDrag.current = null;
              setCropDragging(false);
            }}
            onLostPointerCapture={() => {
              cropDrag.current = null;
              setCropDragging(false);
            }}
            style={
              cropProfile
                ? {
                    aspectRatio: `${cropProfile.width} / ${cropProfile.height}`,
                    borderRadius: cropProfile.round ? '50%' : undefined,
                  }
                : undefined
            }
          >
            {cropPreview && (
              <img
                src={cropPreview}
                alt="图片裁剪预览"
                draggable={false}
                onLoad={(event) =>
                  setCropSize({
                    width: event.currentTarget.naturalWidth,
                    height: event.currentTarget.naturalHeight,
                  })
                }
                style={
                  cropGeometry
                    ? getCropPreviewStyle(cropGeometry, cropX, cropY, cropPreviewScale)
                    : { visibility: 'hidden' }
                }
              />
            )}
          </div>
          <Text type="secondary">按住图片拖动调整位置，触屏可直接拖动；放大后可调整更多区域。</Text>
          <label>
            <span>缩放</span>
            <Slider min={1} max={2.5} step={0.05} value={cropZoom} onChange={setCropZoom} />
          </label>
        </div>
      </Modal>
    </div>
  );
}

function TargetEditor({
  value,
  onChange,
  scope,
}: {
  value: JsonObject;
  onChange: (value: JsonObject) => void;
  scope: EditableContentType;
}) {
  const kind = String(value.kind ?? 'qr');
  const field =
    kind === 'external' ? 'url' : kind === 'route' ? 'path' : kind === 'anchor' ? 'id' : 'slot';
  const qrOptions = [
    { value: 'global-default', label: '全站最终兜底二维码' },
    { value: 'home-consult', label: '首页兜底二维码' },
    { value: 'home-teacher', label: '师资统一二维码' },
    { value: 'teacher-default', label: '师资兜底二维码' },
    { value: 'course-default', label: '课程兜底二维码' },
    { value: 'material-default', label: '资料兜底二维码' },
    { value: 'case-default', label: '出分案例兜底二维码' },
    { value: 'custom', label: '上传新的专用二维码' },
  ];
  const selectedQr = String(value.slot ?? 'global-default');
  if (kind === 'qr' && !qrOptions.some((option) => option.value === selectedQr))
    qrOptions.push({ value: selectedQr, label: `其他二维码：${selectedQr}` });
  return (
    <div className="target-editor">
      <Select
        value={kind}
        options={[
          { value: 'external', label: '打开外部网站' },
          { value: 'route', label: '打开站内页面' },
          { value: 'anchor', label: '跳到本页区块' },
          { value: 'qr', label: '打开二维码弹窗' },
        ]}
        onChange={(next) => {
          const nextField =
            next === 'external'
              ? 'url'
              : next === 'route'
                ? 'path'
                : next === 'anchor'
                  ? 'id'
                  : 'slot';
          onChange({ kind: next, [nextField]: next === 'qr' ? 'global-default' : '' });
        }}
      />
      {kind === 'qr' ? (
        <>
          <Select
            value={selectedQr}
            options={qrOptions}
            onChange={(slot) =>
              onChange({
                kind: 'qr',
                slot,
                ...(slot === 'custom' ? { image: String(value.image ?? '') } : {}),
              })
            }
          />
          {selectedQr === 'custom' && (
            <ImageUploadField
              value={String(value.image ?? '')}
              onChange={(image) => onChange({ kind: 'qr', slot: 'custom', image })}
              scope={scope}
              kind="qr"
            />
          )}
        </>
      ) : (
        <Input
          value={String(value[field] ?? '')}
          onChange={(event) => onChange({ ...value, [field]: event.target.value })}
        />
      )}
    </div>
  );
}

function MarkdownEditor({
  value,
  onChange,
  scope,
}: {
  value: string;
  onChange: (value: string) => void;
  scope: EditableContentType;
}) {
  const editorRef = useRef<TextAreaRef>(null);
  const valueRef = useRef(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const selectionRef = useRef<TextSelection>({ start: value.length, end: value.length });
  const uploadSelections = useRef(new Set<TextSelection>());
  const [pendingCursor, setPendingCursor] = useState<number | null>(null);
  const updateValue = (nextValue: string) => {
    for (const selection of uploadSelections.current) {
      Object.assign(selection, moveSelectionAfterEdit(valueRef.current, nextValue, selection));
    }
    valueRef.current = nextValue;
    onChangeRef.current(nextValue);
  };
  useEffect(() => {
    if (valueRef.current !== value) {
      for (const selection of uploadSelections.current) {
        Object.assign(selection, moveSelectionAfterEdit(valueRef.current, value, selection));
      }
      valueRef.current = value;
    }
    if (pendingCursor === null) return;
    const textarea = editorRef.current?.resizableTextArea?.textArea;
    textarea?.focus({ preventScroll: true });
    textarea?.setSelectionRange(pendingCursor, pendingCursor);
    setPendingCursor(null);
  }, [value, pendingCursor]);
  const rememberSelection = (textarea: HTMLTextAreaElement) => {
    selectionRef.current = { start: textarea.selectionStart, end: textarea.selectionEnd };
  };
  const insert = (text: string, selection = selectionRef.current) => {
    const result = insertAtSelection(valueRef.current, text, selection);
    updateValue(result.value);
    selectionRef.current = { start: result.cursor, end: result.cursor };
    setPendingCursor(result.cursor);
  };
  const imageProps: UploadProps = {
    accept: 'image/jpeg,image/png,image/webp',
    showUploadList: false,
    customRequest: async (options) => {
      const selection = { ...selectionRef.current };
      uploadSelections.current.add(selection);
      try {
        const form = new FormData();
        form.append('file', options.file as File);
        const result = await api<{ public_url: string }>(`/assets/upload?scope=${scope}`, {
          method: 'POST',
          body: form,
        });
        uploadSelections.current.delete(selection);
        insert(`\n\n![图片说明](${result.public_url})\n`, selection);
        options.onSuccess?.(result);
      } catch (error) {
        options.onError?.(error as Error);
      } finally {
        uploadSelections.current.delete(selection);
      }
    },
  };
  return (
    <div className="markdown-field">
      <Alert type="info" showIcon title="在下方编辑资料正文，完整排版效果请查看右侧页面预览。" />
      <div className="markdown-editor-panel">
        <span>资料正文</span>
        <Space
          wrap
          className="markdown-toolbar"
          onMouseDown={() => {
            const textarea = editorRef.current?.resizableTextArea?.textArea;
            if (textarea && document.activeElement === textarea) rememberSelection(textarea);
          }}
        >
          <Button onClick={() => insert('\n\n## 二级标题\n')}>二级标题</Button>
          <Button onClick={() => insert('\n\n### 三级标题\n')}>三级标题</Button>
          <Button onClick={() => insert('\n\n- 列表项\n')}>项目列表</Button>
          <Button
            onClick={() => insert('\n\n| 表头一 | 表头二 |\n| --- | --- |\n| 内容 | 内容 |\n')}
          >
            表格
          </Button>
          <Upload {...imageProps}>
            <Button icon={<UploadOutlined />}>插入图片</Button>
          </Upload>
        </Space>
        <Input.TextArea
          ref={editorRef}
          aria-label="资料正文"
          value={value}
          onSelect={(event) => rememberSelection(event.currentTarget)}
          onFocus={(event) => rememberSelection(event.currentTarget)}
          onBlur={(event) => rememberSelection(event.currentTarget)}
          onChange={(event) => {
            rememberSelection(event.target);
            updateValue(event.target.value);
          }}
          autoSize={{ minRows: 18, maxRows: 18 }}
        />
      </div>
    </div>
  );
}

function move<T>(items: T[], index: number, direction: -1 | 1) {
  const target = index + direction;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
}

function moveTo<T>(items: T[], from: number, to: number) {
  if (from === to) return items;
  const next = [...items];
  const [item] = next.splice(from, 1);
  if (item === undefined) return items;
  next.splice(to, 0, item);
  return next;
}

function ArrayEditor({
  value,
  onChange,
  path,
  scope,
}: {
  value: unknown[];
  onChange: (value: unknown[]) => void;
  path: string[];
  scope: EditableContentType;
}) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const fixed = fixedArrayLength(path);
  const max = maxArrayLength(path);
  const canAdd = !fixed && (max === null || value.length < max);
  const imageList = path.at(-1) === 'images';
  const caseScreenshotList = scope === 'case' && path.join('.') === 'case.shots';
  const valueRef = useRef(value);
  valueRef.current = value;
  const [imageUploadError, setImageUploadError] = useState('');
  const [screenshotUploading, setScreenshotUploading] = useState(false);
  const screenshotUploadLock = useRef(false);
  const screenshotOnChangeRef = useRef(onChange);
  screenshotOnChangeRef.current = onChange;
  const template = emptyItem(path, value[0]);
  const draggable = (index: number) => ({
    draggable: true,
    onDragStart: () => setDraggedIndex(index),
    onDragOver: (event: DragEvent) => event.preventDefault(),
    onDrop: () => {
      if (draggedIndex !== null) onChange(moveTo(value, draggedIndex, index));
      setDraggedIndex(null);
    },
  });
  if (imageList) {
    const uploadHelp = fixed
      ? `固定 ${fixed} 张图片，请在下方图片卡片中逐张上传或替换。`
      : canAdd
        ? `${max === null ? '' : `最多 ${max} 张。`}可一次选择多张图片，也可逐张上传或替换。`
        : `已达到 ${max} 张图片上限，可逐张替换，或先删除已有图片再上传。`;
    const multiUploadProps: UploadProps = {
      accept: 'image/jpeg,image/png,image/webp',
      multiple: true,
      showUploadList: false,
      customRequest: async (options) => {
        try {
          setImageUploadError('');
          const result = await uploadImage(options.file as File, scope);
          const current = valueRef.current;
          if (max !== null && current.length >= max) {
            options.onError?.(new Error(`最多上传 ${max} 张图片`));
            return;
          }
          const next = [...current, result.public_url];
          valueRef.current = next;
          onChange(next);
          options.onSuccess?.(result);
        } catch (uploadError) {
          const message = uploadError instanceof Error ? uploadError.message : '上传失败';
          setImageUploadError(message);
          options.onError?.(uploadError as Error);
        }
      },
    };
    return (
      <div className="structured-list">
        {canAdd && (
          <Upload {...multiUploadProps}>
            <Button type="dashed" block icon={<UploadOutlined />}>
              批量上传图片
            </Button>
          </Upload>
        )}
        <Text type="secondary">
          {uploadHelp}
          {path.join('.') === 'home.chatWall.images' &&
            '电脑端 4 列、平板 3 列、手机 2 列；图片按当前顺序从左到右轮流进入各列。'}
          可拖拽图片卡片排序，也可使用上下移动按钮。
        </Text>
        {imageUploadError && <Alert type="error" showIcon title={imageUploadError} />}
        {value.map((item, index) => (
          <Card
            size="small"
            key={index}
            {...draggable(index)}
            title={
              <Space>
                <DragOutlined />
                图片 {index + 1}
              </Space>
            }
            extra={
              <Space>
                <Button
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={index === 0}
                  onClick={() => onChange(move(value, index, -1))}
                />
                <Button
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={index === value.length - 1}
                  onClick={() => onChange(move(value, index, 1))}
                />
                {!fixed && (
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}
                  />
                )}
              </Space>
            }
          >
            <ImageUploadField
              value={String(item ?? '')}
              scope={scope}
              onChange={(next) =>
                onChange(value.map((current, itemIndex) => (itemIndex === index ? next : current)))
              }
            />
          </Card>
        ))}
        {canAdd && (
          <Button
            type="dashed"
            block
            icon={<PlusOutlined />}
            onClick={() => onChange([...value, ''])}
          >
            添加图片
          </Button>
        )}
      </div>
    );
  }
  if (
    (value.length > 0 && value.every((item) => typeof item === 'string')) ||
    (value.length === 0 && typeof template === 'string')
  ) {
    const multiline = new Set([
      'credentials',
      'experience',
      'path',
      'highlights',
      'included',
      'suitableFor',
      'howToClaim',
      'methods',
    ]).has(path.at(-1) ?? '');
    return (
      <div className={`simple-list${multiline ? ' multiline-list' : ''}`}>
        {value.map((item, index) => (
          <div className="simple-list-row" key={index}>
            {multiline ? (
              <Input.TextArea
                value={String(item)}
                autoSize={{ minRows: 2, maxRows: 6 }}
                onChange={(event) =>
                  onChange(
                    value.map((current, itemIndex) =>
                      itemIndex === index ? event.target.value : current,
                    ),
                  )
                }
              />
            ) : (
              <Input
                value={String(item)}
                onChange={(event) =>
                  onChange(
                    value.map((current, itemIndex) =>
                      itemIndex === index ? event.target.value : current,
                    ),
                  )
                }
              />
            )}
            <Space.Compact>
              <Button
                icon={<ArrowUpOutlined />}
                disabled={index === 0}
                onClick={() => onChange(move(value, index, -1))}
              />
              <Button
                icon={<ArrowDownOutlined />}
                disabled={index === value.length - 1}
                onClick={() => onChange(move(value, index, 1))}
              />
              {!fixed && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}
                />
              )}
            </Space.Compact>
          </div>
        ))}
        {canAdd && (
          <Button
            type="dashed"
            block
            icon={<PlusOutlined />}
            onClick={() => onChange([...value, ''])}
          >
            添加一项
          </Button>
        )}
      </div>
    );
  }
  if (
    (value.length > 0 && value.every((item) => Array.isArray(item))) ||
    (value.length === 0 && Array.isArray(template))
  ) {
    return (
      <div className="structured-list">
        {value.map((row, index) => (
          <Card
            size="small"
            key={index}
            {...draggable(index)}
            title={path.at(-1) === 'faqs' ? `问题 ${index + 1}` : `第 ${index + 1} 行`}
            extra={
              <Space>
                <Button
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={index === 0}
                  onClick={() => onChange(move(value, index, -1))}
                />
                <Button
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={index === value.length - 1}
                  onClick={() => onChange(move(value, index, 1))}
                />
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}
                >
                  删除
                </Button>
              </Space>
            }
          >
            <div className={`array-row-fields${path.at(-1) === 'faqs' ? ' faq-row-fields' : ''}`}>
              {(row as unknown[]).map((cell, cellIndex) => {
                const updateCell = (next: string) =>
                  onChange(
                    value.map((current, rowIndex) =>
                      rowIndex === index
                        ? (current as unknown[]).map((currentCell, currentCellIndex) =>
                            currentCellIndex === cellIndex ? next : currentCell,
                          )
                        : current,
                    ),
                  );
                if (path.at(-1) === 'faqs')
                  return (
                    <label key={cellIndex}>
                      <span>{cellIndex === 0 ? '问题' : '回答'}</span>
                      {cellIndex === 0 ? (
                        <Input
                          value={String(cell ?? '')}
                          onChange={(event) => updateCell(event.target.value)}
                        />
                      ) : (
                        <Input.TextArea
                          value={String(cell ?? '')}
                          autoSize={{ minRows: 4, maxRows: 10 }}
                          onChange={(event) => updateCell(event.target.value)}
                        />
                      )}
                    </label>
                  );
                return (
                  <Input
                    key={cellIndex}
                    value={String(cell ?? '')}
                    placeholder={`第 ${cellIndex + 1} 列`}
                    onChange={(event) => updateCell(event.target.value)}
                  />
                );
              })}
              {path.at(-1) !== 'faqs' && (
                <Button
                  onClick={() =>
                    onChange(
                      value.map((current, rowIndex) =>
                        rowIndex === index ? [...(current as unknown[]), ''] : current,
                      ),
                    )
                  }
                >
                  增加列
                </Button>
              )}
            </div>
          </Card>
        ))}
        {canAdd && (
          <Button
            type="dashed"
            block
            icon={<PlusOutlined />}
            onClick={() => onChange([...value, template])}
          >
            添加一行
          </Button>
        )}
      </div>
    );
  }
  return (
    <div className="structured-list">
      {caseScreenshotList && (
        <>
          <Upload
            accept="image/jpeg,image/png,image/webp"
            multiple
            showUploadList={false}
            disabled={!canAdd || screenshotUploading}
            beforeUpload={async (file, files) => {
              if (file !== files[0] || screenshotUploadLock.current) return Upload.LIST_IGNORE;
              screenshotUploadLock.current = true;
              setScreenshotUploading(true);
              setImageUploadError('');
              try {
                const errors = await uploadCaseScreenshots(
                  files,
                  () => valueRef.current,
                  (next) => {
                    valueRef.current = next;
                    screenshotOnChangeRef.current(next);
                  },
                );
                setImageUploadError(errors.join('；'));
              } finally {
                screenshotUploadLock.current = false;
                setScreenshotUploading(false);
              }
              return Upload.LIST_IGNORE;
            }}
          >
            <Button
              type="dashed"
              block
              icon={<UploadOutlined />}
              disabled={!canAdd}
              loading={screenshotUploading}
            >
              {screenshotUploading ? '正在上传截图' : '批量上传截图'}
            </Button>
          </Upload>
          <Text type="secondary">
            可一次选择多张截图，按选择顺序添加；截图分类和说明默认留空，之后可逐张填写、替换或排序。
            最多 {maxCaseScreenshots} 张，当前 {value.length} 张；支持 JPG、PNG 或 WebP，每张最大
            15MB。
          </Text>
          {imageUploadError && <Alert type="error" showIcon title={imageUploadError} />}
        </>
      )}
      {value.map((item, index) => (
        <Card
          size="small"
          key={index}
          {...draggable(index)}
          title={`项目 ${index + 1}`}
          extra={
            <Space>
              <Button
                size="small"
                icon={<ArrowUpOutlined />}
                disabled={index === 0}
                onClick={() => onChange(move(value, index, -1))}
              />
              <Button
                size="small"
                icon={<ArrowDownOutlined />}
                disabled={index === value.length - 1}
                onClick={() => onChange(move(value, index, 1))}
              />
              {!fixed && (
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => onChange(value.filter((_, itemIndex) => itemIndex !== index))}
                >
                  删除
                </Button>
              )}
            </Space>
          }
        >
          <FieldEditor
            fieldKey={String(index)}
            value={item}
            path={[...path, String(index)]}
            scope={scope}
            onChange={(next) =>
              onChange(value.map((current, itemIndex) => (itemIndex === index ? next : current)))
            }
            hideLabel
          />
        </Card>
      ))}
      {canAdd && (
        <Button
          type="dashed"
          block
          icon={<PlusOutlined />}
          disabled={caseScreenshotList && screenshotUploading}
          onClick={() => onChange([...value, template])}
        >
          添加项目
        </Button>
      )}
    </div>
  );
}

const blockDefaults: Record<string, JsonObject> = {
  list: {
    items: ['示例要点一：这里填写课程提供的内容', '示例要点二：这里填写学员能获得的帮助'],
    emphasisCards: [{ title: '示例重点', text: '这里适合展示需要重点强调的课程优势或服务说明。' }],
  },
  grid: {
    columns: 3,
    cards: [
      { title: '示例卡片一', text: '这里填写第一项课程内容。' },
      { title: '示例卡片二', text: '这里填写第二项课程内容。' },
      { title: '示例卡片三', text: '这里填写第三项课程内容。' },
    ],
    dashedLast: false,
  },
  steps: {
    steps: [
      { title: '第一步', text: '这里填写第一个学习阶段或服务流程。' },
      { title: '第二步', text: '这里填写第二个学习阶段或服务流程。' },
      { title: '第三步', text: '这里填写第三个学习阶段或服务流程。' },
    ],
  },
  table: {
    columns: ['对比项目', '示例方案 A', '示例方案 B'],
    rows: [
      ['课程形式', '示例内容', '示例内容'],
      ['适合人群', '示例内容', '示例内容'],
    ],
    fixedLeft: false,
    highlightColumn: null,
  },
  ref: { className: '', limit: 3 },
};

const courseBlockDefinitions = [
  {
    value: 'list',
    label: '要点列表',
    title: '课程亮点',
    description: '逐条展示要点，可附重点卡片',
  },
  {
    value: 'grid',
    label: '卡片网格',
    title: '课程内容',
    description: '多列卡片展示课程或服务',
  },
  {
    value: 'steps',
    label: '步骤流程',
    title: '学习流程',
    description: '按顺序展示学习或服务流程',
  },
  {
    value: 'table',
    label: '对比表格',
    title: '课程对比',
    description: '展示课表或课程对比',
  },
  {
    value: 'ref',
    label: '出分案例',
    title: '相关出分案例',
    description: '引用已有案例，支持班型筛选',
  },
] as const;

function courseBlockDefinition(type: string) {
  return courseBlockDefinitions.find((item) => item.value === type) ?? courseBlockDefinitions[0];
}

export function createCourseBlockDraft(
  type: string,
  index: number,
  existingAnchors: string[] = [],
) {
  const definition = courseBlockDefinition(type);
  const usedAnchors = new Set(existingAnchors);
  let anchorNumber = index + 1;
  while (usedAnchors.has(`course-section-${anchorNumber}`)) anchorNumber += 1;
  return {
    type: definition.value,
    title: definition.title,
    anchor: `course-section-${anchorNumber}`,
    sort: index,
    data: clone(blockDefaults[definition.value]),
  };
}

function CourseBlocksEditor({
  value,
  onChange,
  scope,
}: {
  value: unknown[];
  onChange: (value: unknown[]) => void;
  scope: EditableContentType;
}) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const updateBlocks = (next: unknown[]) =>
    onChange(next.map((block, sort) => (isObject(block) ? { ...block, sort } : block)));
  return (
    <div className="structured-list course-blocks-editor">
      {value.map((item, index) => {
        const block = isObject(item) ? item : {};
        const blockType = String(block.type ?? 'list');
        const definition = courseBlockDefinition(blockType);
        return (
          <Card
            size="small"
            key={index}
            draggable
            onDragStart={() => setDraggedIndex(index)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => {
              if (draggedIndex !== null) updateBlocks(moveTo(value, draggedIndex, index));
              setDraggedIndex(null);
            }}
            title={
              <div className="course-block-summary">
                <Tag color="orange">{definition.label}</Tag>
                <Text type="secondary">{definition.description}</Text>
              </div>
            }
            extra={
              <Space>
                <Button
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={index === 0}
                  onClick={() => updateBlocks(move(value, index, -1))}
                >
                  上移
                </Button>
                <Button
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={index === value.length - 1}
                  onClick={() => updateBlocks(move(value, index, 1))}
                >
                  下移
                </Button>
                <Button
                  danger
                  size="small"
                  icon={<DeleteOutlined />}
                  onClick={() => updateBlocks(value.filter((_, itemIndex) => itemIndex !== index))}
                >
                  删除
                </Button>
              </Space>
            }
          >
            <div className="field-grid course-block-heading">
              <label>
                <span>区块类型</span>
                <Select
                  value={blockType}
                  options={courseBlockDefinitions.map(({ value, label, description }) => ({
                    value,
                    label,
                    description,
                  }))}
                  optionRender={(option) => (
                    <div className="course-block-option">
                      <strong>{option.label}</strong>
                      <span>{courseBlockDefinition(String(option.value)).description}</span>
                    </div>
                  )}
                  onChange={(nextType) =>
                    updateBlocks(
                      value.map((current, itemIndex) =>
                        itemIndex === index
                          ? { ...block, type: nextType, data: clone(blockDefaults[nextType]!) }
                          : current,
                      ),
                    )
                  }
                />
              </label>
              <label>
                <span>页面模块标题</span>
                <Input
                  value={String(block.title ?? '')}
                  onChange={(event) =>
                    updateBlocks(
                      value.map((current, itemIndex) =>
                        itemIndex === index ? { ...block, title: event.target.value } : current,
                      ),
                    )
                  }
                />
              </label>
            </div>
            <ObjectEditor
              value={isObject(block.data) ? block.data : clone(blockDefaults[blockType] ?? {})}
              path={['blocks', String(index), 'data']}
              scope={scope}
              onChange={(nextData) =>
                updateBlocks(
                  value.map((current, itemIndex) =>
                    itemIndex === index ? { ...block, data: nextData, sort: index } : current,
                  ),
                )
              }
            />
          </Card>
        );
      })}
      <Button
        type="dashed"
        block
        icon={<PlusOutlined />}
        onClick={() =>
          updateBlocks([
            ...value,
            createCourseBlockDraft(
              'list',
              value.length,
              value.flatMap((block) =>
                isObject(block) && typeof block.anchor === 'string' ? [block.anchor] : [],
              ),
            ),
          ])
        }
      >
        添加课程内容区块
      </Button>
    </div>
  );
}

function FieldEditor({
  fieldKey,
  value,
  onChange,
  path,
  scope,
  hideLabel = false,
  lockedFields = [],
  teacherLead = false,
  teacherImage = '',
  imageOrigin,
}: {
  fieldKey: string;
  value: unknown;
  onChange: (value: unknown) => void;
  path: string[];
  scope: EditableContentType;
  hideLabel?: boolean;
  lockedFields?: string[];
  teacherLead?: boolean;
  teacherImage?: string;
  imageOrigin?: string;
}) {
  const validation = fieldValidation(fieldKey, value);
  const help = fieldHelp(fieldKey, path);
  const lockedInternalId =
    fieldKey === 'id' && (scope === 'course' || scope === 'material') && path.length === 2;
  const lockedField = lockedFields.includes(path.slice(1).join('.'));
  let input;
  if (scope === 'teacher' && fieldKey === 'avatarCrop') {
    input = (
      <TeacherAvatarEditor
        key={teacherImage}
        image={teacherImage}
        imageOrigin={imageOrigin}
        value={value}
        onChange={onChange}
      />
    );
  } else if (fieldKey === 'blocks' && Array.isArray(value)) {
    input = <CourseBlocksEditor value={value} onChange={onChange} scope={scope} />;
  } else if (fieldKey === 'body' && typeof value === 'string') {
    input = <MarkdownEditor value={value} onChange={onChange} scope={scope} />;
  } else if (imageKeys.has(fieldKey) && typeof value === 'string') {
    const imageKind =
      path.includes('qrOverride') || path.includes('qrCodes') || path.includes('defaultQr')
        ? 'qr'
        : fieldKey === 'ogImage'
          ? 'share'
          : scope === 'course' && fieldKey === 'cover'
            ? 'course-cover'
            : scope === 'material' && fieldKey === 'cover'
              ? 'material-cover'
              : scope === 'teacher' && fieldKey === 'image'
                ? 'teacher'
                : undefined;
    input = (
      <ImageUploadField
        value={value}
        onChange={onChange}
        scope={scope}
        kind={imageKind}
        imageOrigin={imageOrigin}
      />
    );
  } else if (Array.isArray(value)) {
    input = <ArrayEditor value={value} onChange={onChange} path={path} scope={scope} />;
  } else if (isObject(value) && typeof value.kind === 'string') {
    input = <TargetEditor value={value} onChange={onChange} scope={scope} />;
  } else if (isObject(value)) {
    input = (
      <ObjectEditor
        value={value}
        onChange={onChange}
        path={path}
        scope={scope}
        lockedFields={lockedFields}
      />
    );
  } else if (fieldKey === 'key' && path.includes('aiProducts')) {
    input = (
      <Select
        disabled
        value={String(value ?? '')}
        options={[
          { value: 'plan', label: '备考规划卡片' },
          { value: 'writing', label: 'AI 写作卡片' },
          { value: 'speaking', label: 'AI 口语卡片' },
          { value: 'listening', label: '听力阅读卡片' },
        ]}
      />
    );
  } else if (fieldKey === 'lead' && scope === 'teacher') {
    input = (
      <Select
        value={value ? 'lead' : 'member'}
        options={[
          { value: 'member', label: '普通老师' },
          { value: 'lead', label: '领衔老师' },
        ]}
        onChange={(next) => onChange(next === 'lead')}
      />
    );
  } else if (typeof value === 'boolean') {
    input = <Switch checked={value} onChange={onChange} />;
  } else if (typeof value === 'number' || value === null) {
    input = (
      <InputNumber
        value={value as number | null}
        min={fieldKey === 'highlightColumn' ? 0 : undefined}
        onChange={onChange}
      />
    );
  } else if (enumOptions[fieldKey]) {
    input = (
      <Select value={String(value ?? '')} options={enumOptions[fieldKey]} onChange={onChange} />
    );
  } else if (
    path.join('.') === 'home.hero.title' ||
    longTextKeys.has(fieldKey) ||
    String(value ?? '').length > 100
  ) {
    input = (
      <Input.TextArea
        value={String(value ?? '')}
        autoSize={{ minRows: path.join('.') === 'home.hero.title' ? 2 : 3, maxRows: 10 }}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  } else {
    input = (
      <Input
        disabled={lockedInternalId || lockedField}
        value={String(value ?? '')}
        onChange={(event) => onChange(event.target.value)}
      />
    );
  }
  if (hideLabel) return <>{input}</>;
  const FieldContainer = scope === 'teacher' && fieldKey === 'avatarCrop' ? 'div' : 'label';
  return (
    <FieldContainer className={`structured-field field-${fieldKey}`}>
      <span>{fieldLabel(fieldKey, value, path)}</span>
      {input}
      {help && <Text type="secondary">{help}</Text>}
      {validation && <Text type="danger">{validation}</Text>}
    </FieldContainer>
  );
}

function ObjectEditor({
  value,
  onChange,
  path,
  scope,
  keys,
  lockedFields = [],
  teacherLead = false,
  imageOrigin,
}: {
  value: JsonObject;
  onChange: (value: JsonObject) => void;
  path: string[];
  scope: EditableContentType;
  keys?: string[];
  lockedFields?: string[];
  teacherLead?: boolean;
  imageOrigin?: string;
}) {
  const entries = orderedObjectKeys(value, path, keys);
  return (
    <div className="field-grid">
      {entries.map((key) => {
        const keyPath = key.split('.');
        const fieldKey = keyPath.at(-1)!;
        return (
          <FieldEditor
            key={key}
            fieldKey={fieldKey}
            value={nestedValue(value, key)}
            path={[...path, ...keyPath]}
            scope={scope}
            lockedFields={lockedFields}
            teacherLead={teacherLead}
            teacherImage={
              scope === 'teacher' && path.length === 1 ? String(value.image ?? '') : undefined
            }
            imageOrigin={imageOrigin}
            onChange={(next) => {
              const updated = setNestedValue(value, key, next);
              if (
                scope === 'teacher' &&
                path.length === 1 &&
                key === 'image' &&
                next !== value.image
              )
                delete updated.avatarCrop;
              onChange(updated);
            }}
          />
        );
      })}
    </div>
  );
}

export function ContentEditor({
  type,
  value,
  onChange,
  fieldKeys,
  sectionTitle,
  lockedFields = [],
  imageOrigin,
}: {
  type: EditableContentType;
  value: unknown;
  onChange: (value: unknown) => void;
  fieldKeys?: string[];
  sectionTitle?: string;
  lockedFields?: string[];
  imageOrigin?: string;
}) {
  if (Array.isArray(value)) {
    return (
      <Card
        title={type === 'navigation' ? '导航链接' : '页脚栏目'}
        className="content-section-card"
      >
        <ArrayEditor value={value} onChange={onChange} path={[type]} scope={type} />
      </Card>
    );
  }
  if (!isObject(value))
    return <Alert type="error" showIcon title="内容格式无法识别，请联系管理员" />;
  const configured = fieldKeys
    ? [{ title: sectionTitle ?? '内容配置', keys: fieldKeys }]
    : (contentEditorSections[type] ?? [{ title: '内容配置', keys: Object.keys(value) }]);
  const visibleSections =
    type === 'teacher'
      ? configured.filter(
          (section) => value.lead === true || !section.title.startsWith('领衔老师'),
        )
      : configured;
  const configuredKeys = new Set(
    configured.flatMap((section) => section.keys.map((key) => key.split('.')[0]!)),
  );
  const hiddenKeys = new Set(
    type === 'teacher'
      ? ['id', 'sort']
      : type === 'course' || type === 'material'
        ? type === 'course'
          ? ['id', 'sort', 'qrOverride', 'audience', 'highlights']
          : ['id', 'sort', 'qrOverride', 'eyebrow', 'format']
        : type === 'case'
          ? ['sort', 'qrOverride']
          : type === 'brand'
            ? ['contact']
            : type === 'qr'
              ? ['slot', 'type', 'channelState', 'leadFallback']
              : [],
  );
  const extraKeys = fieldKeys
    ? []
    : Object.keys(value).filter((key) => !configuredKeys.has(key) && !hiddenKeys.has(key));
  const allSections = extraKeys.length
    ? [...visibleSections, { title: '其他设置', keys: extraKeys }]
    : visibleSections;
  return (
    <div className="content-editor-sections">
      {allSections.map((section) => (
        <Card key={section.title} title={section.title} className="content-section-card">
          {section.description && <Text type="secondary">{section.description}</Text>}
          <ObjectEditor
            value={value}
            onChange={onChange}
            path={[type]}
            scope={type}
            keys={section.keys}
            lockedFields={lockedFields}
            teacherLead={type === 'teacher' && value.lead === true}
            imageOrigin={imageOrigin}
          />
        </Card>
      ))}
    </div>
  );
}
